import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useAuth } from "../../../shared/contexts/AuthContext";
import { useLocationContext } from "../../../shared/contexts/useLocationContext";
import MapInitializerNogeo from "../../../shared/location/MapInitializerNogeo";
import { useTranslation } from "react-i18next";
import HighLogo from "../../components/HighLogo";
import logo from "../../../assets/logo.png";
import { nominatim } from "../../../shared/api/nominatim";

// ---------- Types ----------
type NominatimResult = {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  class?: string;
  type?: string;
  address?: {
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    country?: string;
    country_code?: string;
  };
};

type PopularLocation = {
  label: string;
  city: string;
  country: string;
  lat: number;
  lng: number;
};

// ---------- Stable location key ----------
const locationKey = (loc?: {
  city?: string;
  country?: string;
  countryCode?: string;
  coords?: { lat?: number; lng?: number };
}) => {
  if (!loc) return "∅";
  const r = (n?: number) => (Number.isFinite(n!) ? n!.toFixed(2) : "x");
  const city = (loc.city || "").toLowerCase();
  const cc = (loc.countryCode || "").toUpperCase();
  const country = (loc.country || "").toLowerCase();
  const lat = r(loc.coords?.lat);
  const lng = r(loc.coords?.lng);
  const countrySig = cc || country;
  return `${lat},${lng}|${city}|${countrySig}`;
};
const isSameLocation = (a: any, b: any) => locationKey(a) === locationKey(b);


const POPULAR_LOCATIONS: PopularLocation[] = [
  {
    label: "London",
    city: "London",
    country: "United Kingdom",
    lat: 51.5074,
    lng: -0.1278,
  },
  {
    label: "Paris",
    city: "Paris",
    country: "France",
    lat: 48.8566,
    lng: 2.3522,
  },
  {
    label: "Vienna",
    city: "Vienna",
    country: "Austria",
    lat: 48.2082,
    lng: 16.3738,
  },
  {
    label: "Los Angeles",
    city: "Los Angeles",
    country: "United States",
    lat: 34.0522,
    lng: -118.2437,
  },
  {
    label: "New York",
    city: "New York",
    country: "United States",
    lat: 40.7128,
    lng: -74.006,
  },
  {
    label: "Madrid",
    city: "Madrid",
    country: "Spain",
    lat: 40.4168,
    lng: -3.7038,
  },
  {
    label: "São Paulo",
    city: "São Paulo",
    country: "Brazil",
    lat: -23.5505,
    lng: -46.6333,
  },
  {
    label: "Rio de Janeiro",
    city: "Rio de Janeiro",
    country: "Brazil",
    lat: -22.9068,
    lng: -43.1729,
  },
  {
    label: "Mexico City",
    city: "Mexico City",
    country: "Mexico",
    lat: 19.4326,
    lng: -99.1332,
  },
];


// ---------- Component ----------
export default function ChangeLocationNogeo() {
  const navigate = useNavigate();
  const routerLoc = useLocation();
  const { t } = useTranslation();
  const params = new URLSearchParams(routerLoc.search);
  const rawRedirect = params.get("from") || "/menu";
  const redirectTo = rawRedirect.startsWith("/") ? rawRedirect : `/${rawRedirect}`;

  

  const { user } = useAuth();
  const { updateLocation } = useLocationContext();

  const UNKNOWN = t("locationChange.unknown", "Unknown");

  const [location, setLocation] = useState({
    city: UNKNOWN,
    country: UNKNOWN,
    coords: { lat: 0, lng: 0 },
  });

  //Design for mobile
  const [mobileFocus, setMobileFocus] = useState<"search" | "map">("search");
const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
const isSidebarCompact = isMobile && mobileFocus === "map";
const compactScale = isSidebarCompact ? 0.85 : 1;

useEffect(() => {
  const onResize = () => setIsMobile(window.innerWidth < 768);
  window.addEventListener("resize", onResize);
  return () => window.removeEventListener("resize", onResize);
}, []);

const cardGridStyle = {
  display: "grid",
  gridTemplateColumns: isMobile
    ? mobileFocus === "search"
      ? "72% 28%"
      : "28% 72%"
    : "320px minmax(0, 1fr)",
  transition: "grid-template-columns 0.35s ease",
  gap: 12,
  height: "100%",   // ✅ fill wrapper only
};



const sidebarContainerStyle = {
  opacity: isMobile && mobileFocus === "map" ? 0.7 : 1,
  transition: "opacity 0.2s ease",
};

const mapOuterStyle = {
  opacity: isMobile && mobileFocus === "search" ? 0.85 : 1,
  transition: "opacity 0.2s ease",
};

const sidebarCardDynamicStyle = {
  ...sidebarCardStyle,
  padding: isSidebarCompact ? 12 : 18,
  gap: isSidebarCompact ? 10 : 13,
};

const confirmButtonDynamicStyle = {
  ...confirmButtonStyle,
  padding: isSidebarCompact ? "7px 10px" : "10px 14px",
  fontSize: isSidebarCompact ? 10 : 14,
};

const currentLocationButtonDynamicStyle = {
  ...currentLocationButtonStyle,
  padding: isSidebarCompact ? "6px 10px" : "8px 14px",
  fontSize: isSidebarCompact ? 10 : 14,
};

const selectedLocationBoxDynamicStyle = {
  ...selectedLocationBoxStyle,
  padding: isSidebarCompact ? "6px 8px" : "8px 10px",
};


  // Unified search state
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [localMatches, setLocalMatches] = useState<PopularLocation[]>([]);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const mapRef = useRef<L.Map | null>(null);
  const originalKeyRef = useRef<string>("");
  const clickedToEditRef = useRef(false);

  const isAnimatingRef = useRef(false);
  const moveTo = (lat: number, lng: number, zoom = 12) => {
    const m = mapRef.current;
    if (!m || !isFinite(lat) || !isFinite(lng)) return;
    if (lat === 0 && lng === 0) return;
    const cur = m.getCenter();
    if (Math.abs(cur.lat - lat) < 1e-4 && Math.abs(cur.lng - lng) < 1e-4) return;
    if (isAnimatingRef.current) m.stop();
    isAnimatingRef.current = true;
    m.flyTo([lat, lng], zoom, { animate: true, duration: 1.2 });
    m.once("moveend", () => {
      isAnimatingRef.current = false;
    });
  };

  const MapReferenceHandler = () => {
    const m = useMap();
    mapRef.current = m;
    return null;
  };
  

  const GALLERY_HARD_RELOAD_KEY = "gallery:hardReload";

  
  const handleConfirmLocation = async () => {
    const { lat, lng } = location.coords || {};
    if (!lat || !lng) return;

    const safeLocation = {
  city: location.city,
  country: location.country,
  countryCode: (location as any).countryCode,
};

    try {
      const nextKey = locationKey(location);
      const origKey = originalKeyRef.current;

      if (origKey && nextKey === origKey) {
        // unchanged
        sessionStorage.setItem("locchange:result", "unchanged");

        if (redirectTo.startsWith("/gallery")) {
          sessionStorage.setItem(GALLERY_HARD_RELOAD_KEY, "1");
          const bump = redirectTo.includes("?")
            ? `${redirectTo}&refresh=${Date.now()}`
            : `${redirectTo}?refresh=${Date.now()}`;
            
          navigate(bump, { replace: true });
          return;
        }

        navigate(redirectTo);
        return;
      }

      // changed
      await updateLocation(lat, lng, true);
      sessionStorage.setItem("selectedLocation", JSON.stringify(safeLocation));
      sessionStorage.setItem("locchange:result", "changed");

      if (redirectTo.startsWith("/gallery")) {
        sessionStorage.setItem(GALLERY_HARD_RELOAD_KEY, "1");
        const bump = redirectTo.includes("?")
          ? `${redirectTo}&refresh=${Date.now()}`
          : `${redirectTo}?refresh=${Date.now()}`;
        navigate(bump, { replace: true });
      } else {
        navigate(redirectTo);
      }
    } catch (e) {
      console.error("Confirm location failed:", e);
    }
  };

  // -------- “Use Current Location” ⇒ privacy flow (no direct geolocate here) --------
  const handleUseCurrent = () => {
    const deviceUrl = `/change-location/device?from=${encodeURIComponent(redirectTo)}`;
    navigate(`/require-privacy-changeMap?return=${encodeURIComponent(deviceUrl)}`);
  };

   const handlePickPopular = useCallback(
      async (loc: PopularLocation) => {
        const updated = await updateLocation(loc.lat, loc.lng);
        setLocation(updated);
        moveTo(loc.lat, loc.lng, 12);
    
        const label = `${
          updated.city !== UNKNOWN ? updated.city : loc.city
        }, ${
          updated.country !== UNKNOWN ? updated.country : loc.country
        }`;
    
        setQuery("");
        setResults([]);
        setOpen(false);
        setActiveIndex(-1);
        inputRef.current?.blur();
      },
      [updateLocation, UNKNOWN]
    );
    
  
  // -------- Map interactions --------
  const handlePinDragEnd = async (e: L.DragEndEvent) => {
      const { lat, lng } = (e.target as L.Marker).getLatLng();
      const updated = await updateLocation(lat, lng);
      setLocation(updated);
      moveTo(lat, lng, 12);
    };

  const MapClickHandler = () => {
      useMapEvents({
        click: async (e) => {
          const { lat, lng } = e.latlng;
          const updated = await updateLocation(lat, lng);
          setLocation(updated);
          moveTo(lat, lng, 13);
        },
      });
      return null;
    };


  // -------- Initial load from sessionStorage (no geolocate) --------
  useEffect(() => {
    if (!user) return;
    const stored = sessionStorage.getItem("selectedLocation");
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      if (parsed?.city && parsed?.country) {
  setLocation((prev) => ({
    ...prev,
    city: parsed.city,
    country: parsed.country,
    countryCode: parsed.countryCode,
  }));
  originalKeyRef.current = locationKey(parsed);
}
    } catch {
      // ignore
    }
  }, [user]);

  // -------- Unified search (city or country) --------

  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      setLocalMatches([]);
      return;
    }
  
    const normalized = q.replace(/\s+/g, ""); // "newy" matches "New York"
  
    const matches = POPULAR_LOCATIONS.filter((pl) => {
      const labelNorm = pl.label.toLowerCase().replace(/\s+/g, "");
      return labelNorm.startsWith(normalized);
    });
  
    setLocalMatches(matches);
  }, [query]);

  
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    const ctrl = new AbortController();
    const tId = setTimeout(async () => {
      setLoading(true);
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=8&q=${encodeURIComponent(
          q
        )}`;
        const res = await nominatim.get("/search", {
  params: {
    format: "json",
    addressdetails: 1,
    limit: 8,
    q,
  },
});
const data: NominatimResult[] = res.data;

        setResults(data);
        setOpen(data.length > 0);
        setActiveIndex(data.length ? 0 : -1);
      } catch (e: any) {
        if (e.name !== "AbortError") console.error("Search failed:", e);
      } finally {
        setLoading(false);
      }
    }, 280);
    return () => {
      clearTimeout(tId);
      ctrl.abort();
    };
  }, [query]);

  // click outside to close
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!open) return;
      const tNode = e.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(tNode) &&
        inputRef.current &&
        !inputRef.current.contains(tNode)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // pick a result
 const pickResult = useCallback(
    async (r: NominatimResult) => {
      const lat = parseFloat(r.lat);
      const lng = parseFloat(r.lon);
      const updated = await updateLocation(lat, lng);
      setLocation(updated);
      moveTo(lat, lng, 12);

      const short =
  updated.city !== UNKNOWN
    ? `${updated.city}, ${updated.country}`
    : r.display_name.split(",").slice(0, 2).join(", ");
// clear the search box after selection
setQuery("");
setResults([]);
setOpen(false);
setActiveIndex(-1);
inputRef.current?.blur();
    },
    [updateLocation, UNKNOWN]
  );


  // keyboard navigation for search
  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (e.key === "ArrowDown" && (results.length || localMatches.length)) {
        e.preventDefault();
        setOpen(true);
        setActiveIndex(results.length ? 0 : -1);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (results.length ? (i + 1) % results.length : -1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) =>
        results.length ? (i - 1 + results.length) % results.length : -1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && results[activeIndex]) pickResult(results[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // UI label helpers
  const badgeFor = (r: NominatimResult) => {
    const tType = (r.type || "").toLowerCase();
    if (tType === "country") return t("locationChange.badge.country");
    if (tType === "state") return t("locationChange.badge.state");
    if (["city", "town"].includes(tType)) return t("locationChange.badge.city");
    if (tType === "village") return t("locationChange.badge.village");
    if (tType === "suburb") return t("locationChange.badge.suburb");
    return r.type
      ? r.type[0].toUpperCase() + r.type.slice(1)
      : t("locationChange.badge.place");
  };
  const primaryLabel = (r: NominatimResult) => {
    const a = r.address || {};
    return (
      a.city ||
      a.town ||
      a.village ||
      a.state ||
      a.country ||
      r.display_name.split(",")[0]
    );
  };

  const confirmDisabled = !location.coords.lat || !location.coords.lng;

  return (
    <div style={pageShellStyle}>
      {/* Header with logo + close */}
      <header style={pageHeaderStyle}>
        <div
  style={pageHeaderLeft}
  onClick={() => navigate("/gallery")}
>
  <HighLogo
    src={logo}
    style={{ width: 118, display: "block", marginLeft: "-20px", marginTop: "6px" }}
    padding={0}
  />
</div>
      </header>

      <main style={pageMainStyle}>
        <div style={panelsWrapperStyle}>
  <div style={cardGridStyle}>
          {/* Sidebar */}
          <aside
  style={sidebarContainerStyle}
  onClick={() => isMobile && setMobileFocus("search")}
>


            <div style={sidebarCardDynamicStyle}>
              <h2 style={{
  ...sidebarHeadingStyle,
  fontSize: isSidebarCompact ? 15 : 18
}}>
                {t("locationChange.sidebar.title")}
              </h2>

              {/* Selected location */}
              <div style={selectedLocationBoxDynamicStyle}>
                <div style={selectedLabelTitleStyle}>
                  {t("locationChange.labels.selectedLabel", "Selected")}
                </div>
                 <div style={{
  ...selectedLabelStyle,
  fontSize: isSidebarCompact ? 10 : 14
}}>
                  {t("locationChange.labels.selected", {
                    city: location.city,
                    country: location.country,
                  })}
                </div>
              </div>

              {/* Search */}
              {!isSidebarCompact && (
                <div style={overlaySearchWrapperStyle} ref={popoverRef}>
                <div style={{ position: "relative" }}>
                  <input
                    ref={inputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => {
                      if (clickedToEditRef.current && results.length) setOpen(true);
                    }}
                    onBlur={() => {
                      setTimeout(() => {
                        setOpen(false);
                        clickedToEditRef.current = false;
                      }, 120);
                    }}
                    onKeyDown={onInputKeyDown}
                    onPointerDown={() => {
                      clickedToEditRef.current = true;
                    }}
                    placeholder={t("locationChange.search.placeholder")}
                    style={unifiedInputStyle}
                    aria-expanded={open}
                    aria-autocomplete="list"
                    aria-controls="geo-search-listbox"
                    role="combobox"
                  />
                  {query && (
                    <button
                      onClick={() => {
                        setQuery("");
                        setResults([]);
                        setOpen(false);
                        setActiveIndex(-1);
                        inputRef.current?.focus();
                      }}
                      style={clearBtnStyle}
                      aria-label={t("locationChange.search.clearAria")}
                    >
                      ×
                    </button>
                  )}
                </div>

                {/* Dropdown */}
                {open && (
  <div style={dropdownCardStyle}>
    {loading && (
      <div style={dropdownHintStyle}>
        {t("locationChange.search.searching")}
      </div>
    )}

    {!loading && results.length === 0 && localMatches.length === 0 && (
      <div style={dropdownHintStyle}>
        {t("locationChange.search.noResults")}
      </div>
    )}

    {!loading && (localMatches.length > 0 || results.length > 0) && (
      <ul
        id="geo-search-listbox"
        role="listbox"
        aria-label={t("locationChange.search.ariaSuggestions")}
        style={listStyle}
      >
        {/* 1) Popular / local matches first */}
        {localMatches.map((pl) => (
          <li
            key={`popular-${pl.label}`}
            role="option"
            aria-selected={false}
            onMouseDown={(e) => {
              e.preventDefault();
              handlePickPopular(pl);
            }}
            style={itemStyle}
            title={`${pl.city}, ${pl.country}`}
          >
            <div style={rowTopStyle}>
              <span style={nameStyle}>{pl.label}</span>
              <span style={badgeStyle}>
                {t("locationChange.badge.popular", "Popular")}
              </span>
            </div>
            <div style={subLabelStyle}>
              {pl.city}, {pl.country}
            </div>
          </li>
        ))}

        {/* 2) Nominatim results afterwards */}
        {results.map((r, i) => {
          const active = i === activeIndex;
          return (
            <li
              key={r.place_id}
              role="option"
              aria-selected={active}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                pickResult(r);
              }}
              style={{
                ...itemStyle,
                ...(active ? itemActiveStyle : null),
              }}
              title={r.display_name}
            >
              <div style={rowTopStyle}>
                <span style={nameStyle}>{primaryLabel(r)}</span>
                <span style={badgeStyle}>{badgeFor(r)}</span>
              </div>
              <div style={subLabelStyle}>{r.display_name}</div>
            </li>
          );
        })}
      </ul>
    )}
  </div>
)}
              </div>
              )}

              {/* Popular suggestions */}
{!isSidebarCompact && (
  <div style={popularBoxStyle}>

  <div style={popularTitleStyle}>
    {t("locationChange.popular.title", "Quick suggestions")}
  </div>
  <div style={popularChipsWrapperStyle}>
    {POPULAR_LOCATIONS.map((pl) => (
      <button
        key={pl.label}
        type="button"
        style={popularChipStyle}
        onClick={() => handlePickPopular(pl)}
      >
        {pl.label}
      </button>
    ))}
  </div>
</div>
)}

             

              {/* Spacer */}
              <div style={{ flex: 1 }} />

              {/* Confirm */}
              <div style={sidebarActionsStyle}>
                <button
                  onClick={handleConfirmLocation}
                  disabled={confirmDisabled}
                  style={{
                    ...confirmButtonStyle,
                    opacity: confirmDisabled ? 0.65 : 1,
                    cursor: confirmDisabled ? "not-allowed" : "pointer",
                  }}
                >
                  {t("locationChange.actions.confirm")}
                </button>

                 {/* Use current location (privacy flow) */}
                <button
                  type="button"
                  onClick={handleUseCurrent}
                  style={currentLocationButtonDynamicStyle}
                >
                  {t(
                    "locationChange.actions.useCurrentLocation",
                    "Use current location"
                  )}
                </button>
              </div>
            </div>
          </aside>

          {/* Map area */}
          <section
  style={mapOuterStyle}
  onClick={() => isMobile && setMobileFocus("map")}
>


            <div style={mapCardStyle}>
              <div style={mapWrapperStyle}>
                <MapContainer
                  center={[20, 0]} // neutral world view
                  zoom={2}
                  style={mapStyle}
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <MapReferenceHandler />
                  <MapInitializerNogeo setLocation={setLocation} />
                  <MapClickHandler />
                  {location.coords.lat !== 0 && location.coords.lng !== 0 && (
                    <Marker
                      position={[location.coords.lat, location.coords.lng]}
                      draggable
                      eventHandlers={{ dragend: handlePinDragEnd }}
                    />
                  )}
                </MapContainer>
              </div>
            </div>
          </section>
        </div>
        </div>
      </main>
    </div>
  );
}

/* ---------- Styles (mirrors ChangeLocationGeo) ---------- */

const pageShellStyle: React.CSSProperties = {
  minHeight: "100vh",
  height: "100vh",
  width: "100%",
  background:
    "radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)",
  color: "#e5e7eb",
  fontFamily:
    'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const pageHeaderStyle: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 30,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 24px 4px",
  background: "rgba(2, 6, 23, 0.4)",
  backdropFilter: "blur(10px)",
};

const pageHeaderLeft: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  cursor: "pointer",
  marginLeft: -20,
};


const pageMainStyle: React.CSSProperties = {
  flex: 1,
  padding: "-5px 4px 8px",
};

const panelsWrapperStyle: React.CSSProperties = {
  height: "76vh",          // ✅ controls total panel height
  maxHeight: "760px",      // optional desktop cap
  minHeight: "420px",      // optional mobile safety
  marginTop: 6,
};


/* Sidebar card */
const sidebarCardStyle: React.CSSProperties = {
  height: "100%",
  background:
    "linear-gradient(145deg, rgba(4, 7, 15, 0.96), rgba(15,23,42,0.99))",
  color: "#e5e7eb",
  display: "flex",
  flexDirection: "column",
  gap: 14,
  borderRadius: 20,
  border: "1px solid rgba(148,163,184,0.35)",
  boxShadow: "0 22px 50px rgba(0,0,0,0.8)",
  padding: 18,
};

const sidebarHeadingStyle: React.CSSProperties = {
  marginBottom: 4,
  marginTop: -4,
  fontSize: 18,
  fontWeight: 600,
  letterSpacing: -0.02,
};

/* Selected location box */
const selectedLocationBoxStyle: React.CSSProperties = {
  background: "rgba(15,23,42,0.85)",
  borderRadius: 14,
  padding: "8px 10px",
  border: "1px solid rgba(148,163,184,0.4)",
};

const selectedLabelTitleStyle: React.CSSProperties = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.08,
  color: "#9ca3af",
  marginBottom: 4,
};

const selectedLabelStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
  color: "#cbd5f5",
};

/* Search wrapper */
const overlaySearchWrapperStyle: React.CSSProperties = {
  position: "relative",
  marginTop: 8,
};

const unifiedInputStyle: React.CSSProperties = {
  width: "84%",
  padding: "10px 38px 10px 12px",
  borderRadius: 999,
  border: "1px solid #2a3444",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))",
  color: "#e5eefb",
  outline: "none",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
  fontSize: 14,
};

const clearBtnStyle: React.CSSProperties = {
  position: "absolute",
  right: 10,
  top: "50%",
  transform: "translateY(-50%)",
  border: "none",
  background: "transparent",
  color: "#cbd5e1",
  fontSize: 18,
  cursor: "pointer",
};

/* Dropdown under search */
const dropdownCardStyle: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 6px)",
  left: 0,
  right: 0,
  background: "#0b0f17",
  border: "1px solid #22304a",
  borderRadius: 10,
  boxShadow: "0 10px 28px rgba(0,0,0,0.35)",
  zIndex: 2000,
  maxHeight: 320,
  overflow: "hidden",
};

const listStyle: React.CSSProperties = {
  listStyle: "none",
  padding: 0,
  margin: 0,
  maxHeight: 320,
  overflowY: "auto",
};

const itemStyle: React.CSSProperties = {
  padding: "10px 12px",
  cursor: "pointer",
  borderBottom: "1px solid #0f1827",
};

const itemActiveStyle: React.CSSProperties = {
  background:
    "linear-gradient(180deg, rgba(37,99,235,0.18), rgba(37,99,235,0.08))",
};

const rowTopStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
};

const badgeStyle: React.CSSProperties = {
  fontSize: 11,
  color: "#9db7ff",
  background: "rgba(37,99,235,0.18)",
  border: "1px solid rgba(37,99,235,0.35)",
  padding: "2px 6px",
  borderRadius: 999,
  whiteSpace: "nowrap",
};

const nameStyle: React.CSSProperties = {
  color: "#e5eefb",
  fontWeight: 600,
};

const subLabelStyle: React.CSSProperties = {
  marginTop: 2,
  color: "#9ab0cc",
  fontSize: 12,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const dropdownHintStyle: React.CSSProperties = {
  padding: "12px",
  color: "#9ab0cc",
  fontSize: 13,
};

/* Use current location block */
const useCurrentWrapperStyle: React.CSSProperties = {
  marginTop: 10,
};

const currentLocationButtonStyle: React.CSSProperties = {
  padding: "8px 14px",
  fontSize: 13,
  borderRadius: 999,
  border: "1px solid rgba(148,163,184,0.6)",
  background: "rgba(15,23,42,0.9)",
  color: "#e5e7eb",
  cursor: "pointer",
};

/* Sidebar actions */
const sidebarActionsStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  marginTop: 0,
};

const mapCardStyle: React.CSSProperties = {
  background:
    "linear-gradient(145deg, rgba(4,7,15,0.98), rgba(4,7,15,0.96))",
  borderRadius: 22,
  padding: "14px",
  border: "1px solid rgba(148,163,184,0.35)",
  boxShadow: "0 22px 50px rgba(0,0,0,0.85)",
  position: "relative",
  display: "flex",
  flexDirection: "column",
  height: "100%",
};

/* Map */
const mapWrapperStyle: React.CSSProperties = {
  borderRadius: 18,
  overflow: "hidden",
  boxShadow: "0 12px 30px rgba(0,0,0,0.7)",
  flex: 1,
};

const mapStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
};

/* Confirm button */
const confirmButtonStyle: React.CSSProperties = {
  padding: "10px 14px",
  background: "linear-gradient(135deg, #22c55e, #a3e635)",
  color: "#0b1120",
  border: "none",
  borderRadius: 999,
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 14,
};

const popularBoxStyle: React.CSSProperties = {
  marginTop: 10,
};

const popularTitleStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#9ca3af",
  marginBottom: 4,
};

const popularChipsWrapperStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
};

const popularChipStyle: React.CSSProperties = {
  padding: "5px 10px",
  borderRadius: 999,
  border: "1px solid rgba(148,163,184,0.7)",
  background: "rgba(15,23,42,0.95)",
  color: "#e5e7eb",
  fontSize: 12,
  cursor: "pointer",
};

